import {
  Alert,
  Center,
  em,
  Flex,
  Group,
  GroupProps,
  LoadingOverlay,
  Pagination as MantinePagination,
  PaginationProps,
  Select,
  SimpleGrid,
  Text,
  TextProps,
  useMatches,
} from '@mantine/core'
import { useDebouncedValue, useIsFirstRender, useMediaQuery } from '@mantine/hooks'
import CenterLoader from 'common/Loader'
import { useSearchProducts } from 'hooks/products'
import { useEffect } from 'react'
import { Route } from '../../routes/_auth/index'
import ProductCard from './ProductCard'

type Props = {
  searchSymbol: string
}

export default function ProductsList({ searchSymbol }: Props) {
  const navigate = Route.useNavigate()
  const { page, pageSize } = Route.useSearch()
  const columnsCount = useColumnsCount(pageSize)

  const firstRender = useIsFirstRender()
  const [debouncedSearch] = useDebouncedValue(searchSymbol, 500)

  useEffect(() => {
    navigate({
      search: (oldSearch: any) => ({ ...oldSearch, searchText: debouncedSearch, page: firstRender ? page : 1 }),
    })
  }, [debouncedSearch])

  const { data, isFetching, isError, isSuccess } = useSearchProducts({
    symbol: debouncedSearch,
    get: pageSize,
    skip: (page - 1) * pageSize,
  })

  useEffect(() => {
    if (isSuccess && data.data.length === 0 && page > 1) {
      navigate({
        search: (oldSearch: any) => ({ ...oldSearch, page: 1 }),
      })
    }
  }, [isSuccess, data?.data, pageSize])

  if (firstRender && isFetching) return <CenterLoader h="200px" />
  if (isError) return <Alert color="red">Wystąpił błąd podzas wczytywania produktów</Alert>
  else if (isSuccess) {
    return (
      <>
        <SimpleGrid cols={columnsCount} mt="lg" pos="relative">
          <LoadingOverlay visible={isFetching} zIndex={1000} overlayProps={{ radius: 'sm', blur: 2 }} />
          {data.data.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </SimpleGrid>
        {data.total === 0 ? (
          <Center mt="xl">
            <Text c="dimmed">Nie znaleziono produktów o podanym symbolu</Text>
          </Center>
        ) : (
          <>
            <Flex mt="xl" direction="column" gap="md" hiddenFrom="md">
              <Center>
                <Pagination total={data.total} />
              </Center>
              <Group justify="space-between">
                <ProductCount total={data.total} />
                <PageSizeSelect />
              </Group>
            </Flex>

            <Group mt="xl" justify="center" visibleFrom="md">
              <ProductCount total={data.total} mr="auto" />
              <Pagination pos="absolute" total={data.total} />
              <PageSizeSelect ml="auto" />
            </Group>
          </>
        )}
      </>
    )
  }
}

function Pagination({ total, ...props }: { total: number } & PaginationProps) {
  const navigate = Route.useNavigate()
  const { page, pageSize } = Route.useSearch()

  return (
    <MantinePagination
      value={page}
      onChange={(page) => navigate({ search: (oldValue: any) => ({ ...oldValue, page }) })}
      total={Math.ceil(total / pageSize)}
      {...props}
    />
  )
}

function PageSizeSelect(props: GroupProps) {
  const navigate = Route.useNavigate()
  const { pageSize } = Route.useSearch()

  return (
    <Group gap="xs" {...props}>
      <Text>Rozmiar strony</Text>
      <Select
        checkIconPosition="right"
        data={['6', '8', '10', '12']}
        w={70}
        value={pageSize.toString()}
        onChange={(pageSize) => {
          if (pageSize) {
            navigate({ search: (oldValue: any) => ({ ...oldValue, pageSize: parseInt(pageSize) }) })
          }
        }}
      />
    </Group>
  )
}

function ProductCount({ total, ...props }: { total: number } & TextProps) {
  return <Text {...props}>{total} produktów</Text>
}

function useColumnsCount(pageSize: number) {
  const isMobile = useMediaQuery(`(max-width: ${em(400)})`)
  const offset6: number = useMatches({
    xl: 0,
    lg: 0,
    md: 0,
    sm: -1,
    xs: -0,
    base: -1,
  })

  const offset8: number = useMatches({
    xl: 0,
    lg: 0,
    md: -1,
    sm: -2,
    xs: -1,
    base: -2,
  })

  const offset10: number = useMatches({
    xl: 0,
    lg: -1,
    md: -2,
    sm: -3,
    xs: -2,
    base: -3,
  })

  if (isMobile) return 1

  switch (pageSize) {
    case 6:
      return 3 + offset6

    case 8:
      return 4 + offset8

    case 10:
      return 5 + offset10

    case 12:
      return 4 + offset8

    default:
      return 4 + offset8
  }
}
